import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CollectionsTable from "@/components/admin/collections/CollectionsTable";
import CollectionLoadingState from "@/components/collections/CollectionLoadingState";
import {
  COLLECTIONS_EMPTY_TEXT,
  COLLECTIONS_EMPTY_TITLE,
  LOAD_ERROR,
  platformDisabledToast,
  platformEnabledToast,
  REFRESH_ERROR,
  RETRY_LABEL,
  TOGGLE_FALLBACK,
} from "@/constants/adminCollections";
import {
  ADMIN_COLLECTIONS_KEY,
  useAdminCollections,
  useSetPlatformEnabled,
} from "@/hooks/useAdminCollections";
import type { AdminCollectionRow } from "@/interfaces/adminCollections";
import { apiErrorMessage } from "@/utils/apiError-helper";

interface ToastState {
  message: string;
  severity: "success" | "error";
}

/**
 * GFG Admin -> Collections (GFG-388). The topbar already reads "Collections", so there is no
 * heading here. Toggles are not optimistic: the row keeps its old state until the server agrees.
 */
export default function AdminCollections() {
  const { data, isLoading, isError } = useAdminCollections();
  const setPlatformEnabled = useSetPlatformEnabled();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [toast, setToast] = useState<ToastState | null>(null);

  const setPending = (id: string, pending: boolean) =>
    setPendingIds((current) => {
      const next = new Set(current);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleToggle = async (row: AdminCollectionRow, next: boolean) => {
    setPending(row.id, true);
    try {
      await setPlatformEnabled.mutateAsync({ id: row.id, enabled: next });
      setToast({
        message: next ? platformEnabledToast(row.name) : platformDisabledToast(row.name),
        severity: "success",
      });
    } catch (error: unknown) {
      setToast({ message: apiErrorMessage(error, TOGGLE_FALLBACK), severity: "error" });
    } finally {
      setPending(row.id, false);
    }
  };

  const handleOpen = (row: AdminCollectionRow) =>
    navigate(`/admin/collections/${encodeURIComponent(row.id)}`);

  const retry = () => void queryClient.invalidateQueries({ queryKey: ADMIN_COLLECTIONS_KEY });
  const retryButton = (
    <Button color="inherit" size="small" onClick={retry}>
      {RETRY_LABEL}
    </Button>
  );

  const renderBody = () => {
    if (isLoading) return <CollectionLoadingState />;
    // A failed background refetch keeps the table; only a load with nothing to show blocks.
    if (isError && !data) {
      return (
        <Alert severity="error" role="alert" action={retryButton}>
          {LOAD_ERROR}
        </Alert>
      );
    }
    if (!data || data.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
            {COLLECTIONS_EMPTY_TITLE}
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#717171" }}>
            {COLLECTIONS_EMPTY_TEXT}
          </Typography>
        </Box>
      );
    }
    return (
      <>
        {isError && (
          <Alert severity="warning" role="alert" action={retryButton} sx={{ mb: 2 }}>
            {REFRESH_ERROR}
          </Alert>
        )}
        <CollectionsTable
          rows={data}
          pendingIds={pendingIds}
          onToggle={(row, next) => void handleToggle(row, next)}
          onOpen={handleOpen}
        />
      </>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: "var(--font-lato)" }}>
      {renderBody()}
      <Snackbar
        open={toast !== null}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast?.severity ?? "success"}
          variant="filled"
          onClose={() => setToast(null)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
